import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import React, { useEffect, useMemo, useState } from "react";
import Panel from "../../../common/components/Panel";
import Spinner from "../../../common/components/Spinner";
import { useErrorHandler } from "../../../common/hooks";
import { MapDisplayModel } from "../../Models/MapDisplayModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

type Location = {
  latitude: number;
  longitude: number;
};

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const MapDisplayComponent: React.FC<IComponentProps<MapDisplayModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const [location, setLocation] = useState<Location | null>(null);
  const [locationPending, setLocationPending] = useState<boolean>(false);

  const handleLoadLocation = async () => {
    if (!component.GetLocation) {
      console.warn("GetLocation expression is not defined.");
      return;
    }

    setLocationPending(true);
    try {
      const result = await context.Evaluator.evaluate(component.GetLocation, context);
      setLocation(result);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLocationPending(false);
    }
  };

  // Load location on start
  useEffect(() => {
    handleLoadLocation();
  }, [component.GetLocation]);

  const center = useMemo(
    () =>
      location?.latitude && location?.longitude
        ? ({
            lat: location.latitude,
            lng: location.longitude,
          } as google.maps.LatLngLiteral)
        : component.DefaultLatitude && component.DefaultLongitude
          ? ({
              lat: component.DefaultLatitude,
              lng: component.DefaultLongitude,
            } as google.maps.LatLngLiteral)
          : undefined,
    [location, component]
  );

  const componentLayoutSx = getComponentLayout(component);

  return (
    <Panel headerLabel={component.HeaderLabel} headerIconName={component.HeaderIconName} sx={componentLayoutSx}>
      {(locationPending || !isLoaded) && <Spinner />}
      {!locationPending && isLoaded && center && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={component.Zoom || 13}
          options={{
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
          }}
        >
          <Marker position={center} title="Location Marker" />
        </GoogleMap>
      )}
    </Panel>
  );
};

export default MapDisplayComponent;
